import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Button, Card, Input, MotifBackground, FAQ, Badge, LeakyBucket,
  TimeLeakIcon,
} from '@/kindra';
import { CheckCircle2, ArrowRight, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round to nearest $100 (per Build Spec).
const round100 = (n) => Math.round(n / 100) * 100;

// Analytics shim — fires Plausible / PostHog events if window.kindraTrack is wired (Phase 1.5)
const track = (name, props = {}) => {
  try {
    if (typeof window !== 'undefined' && typeof window.kindraTrack === 'function') {
      window.kindraTrack(name, props);
    }
    // eslint-disable-next-line no-console
    console.debug('[kindra-event]', name, props);
  } catch { /* noop */ }
};

const VPH_CHIPS = [80, 120, 180, 250];

// =============================================================================
// /admin-tax-calculator — full page per KINDRA_ADMIN_TAX_CALCULATOR_BUILD
// Live result, client-side formula, soft email capture, Time/magenta-only bucket.
// Bundle target <50KB — no heavy libs.
// =============================================================================
export default function AdminTaxCalculatorPage() {
  const startedRef = useRef(false);
  const completedRef = useRef(false);

  const [hours, setHours] = useState(10);
  const [vph, setVph] = useState(120);
  const [weeks, setWeeks] = useState(48);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const onChange = (setter) => (e) => {
    if (!startedRef.current) {
      track('calc_started');
      startedRef.current = true;
    }
    setter(typeof e === 'number' ? e : Number(e.target.value));
  };

  // Formulae per spec
  const hoursLost = hours * weeks;
  const directCost = round100(hoursLost * 40);
  const trueCost = round100(hoursLost * vph);
  const fiveYearCost = round100(trueCost * 5);

  useEffect(() => {
    // Fire calc_completed once after first meaningful input
    if (startedRef.current && !completedRef.current && trueCost > 0) {
      // small debounce — fire 500ms after last change
      const t = setTimeout(() => {
        completedRef.current = true;
        track('calc_completed', { hours, vph, weeks, trueCost });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [hours, vph, weeks, trueCost]);

  // Time-only bucket — every other leak score = 100 (sealed), Time score derives from inverse cost intensity.
  // Use a score 0–100 where higher = more leak. We map trueCost up to $200k → 100.
  const timeScore = useMemo(() => {
    return Math.max(0, Math.min(100, Math.round((trueCost / 200000) * 100)));
  }, [trueCost]);
  const bucketScores = useMemo(() => ({
    time: 100 - timeScore, // LeakyBucket internal: low score = bigger leak
    money: 100, lostwork: 100, pricing: 100, blindspot: 100,
  }), [timeScore]);

  return (
    <>
      <Hero />
      <section className="bg-[#F5F8FC] k-light">
        <div className="k-container k-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* LEFT — controls */}
            <div className="lg:col-span-7">
              <Card className="p-6 md:p-8" data-testid="calc-controls">
                <p className="k-eyebrow text-[var(--muted)] mb-2">Your numbers</p>
                <h2 className="k-h2 text-[var(--ink)]">Three quick inputs.</h2>

                <div className="mt-7">
                  <label htmlFor="ah" className="k-small font-semibold text-[var(--ink)]">Admin hours per week</label>
                  <div className="flex items-baseline gap-3 mt-1">
                    <span className="k-numeric-xl k-spectrum-text" data-testid="calc-hours-display">{hours}</span>
                    <span className="k-body-l text-[var(--muted)]">hrs / week</span>
                  </div>
                  <input
                    id="ah" type="range" min="1" max="25" value={hours}
                    onChange={onChange(setHours)}
                    className="w-full mt-2 accent-[#EB258D]"
                    data-testid="calc-hours-slider"
                    aria-label="Admin hours per week"
                  />
                  <div className="flex justify-between k-small text-[var(--muted)]"><span>1</span><span>25</span></div>
                </div>

                <div className="mt-7">
                  <p className="k-small font-semibold text-[var(--ink)]">Your value per hour</p>
                  <p className="k-small text-[var(--muted)]">Roughly what your time would charge out at — or what you'd pay someone good to do this job.</p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="calc-vph-chips">
                    {VPH_CHIPS.map((v) => (
                      <button
                        key={v} type="button"
                        onClick={() => { if (!startedRef.current) { track('calc_started'); startedRef.current = true; } setVph(v); }}
                        className={`h-10 px-4 rounded-pill k-small font-semibold border transition-colors ${vph === v ? 'bg-navy text-icy border-navy' : 'bg-white text-[var(--ink)] border-[var(--line)] hover:bg-[var(--tint)]'}`}
                        data-testid={`calc-vph-${v}`}
                      >${v}/hr</button>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowAdvanced(v => !v)}
                  className="mt-7 inline-flex items-center gap-2 k-small font-semibold text-[var(--ink)] hover:opacity-80"
                  aria-expanded={showAdvanced}
                  data-testid="calc-advanced-toggle"
                >
                  <Info size={14} /> {showAdvanced ? 'Hide' : 'Tweak'} working weeks
                </button>
                {showAdvanced && (
                  <div className="mt-3" data-testid="calc-advanced">
                    <label htmlFor="ww" className="k-small font-semibold text-[var(--ink)]">Working weeks per year</label>
                    <input id="ww" type="range" min="40" max="52" value={weeks} onChange={onChange(setWeeks)} className="w-full mt-1 accent-[#EB258D]" data-testid="calc-weeks-slider" />
                    <div className="flex justify-between k-small text-[var(--muted)]"><span>40</span><span className="text-[var(--ink)] font-display font-bold">{weeks}</span><span>52</span></div>
                  </div>
                )}

                <p className="k-small text-[var(--muted)] mt-6">
                  Maths: hours per week × working weeks × your value per hour. Rounded to the nearest $100. No backend, no tracking until you ask us to send the takeaway.
                </p>
              </Card>
            </div>

            {/* RIGHT — live result */}
            <div className="lg:col-span-5">
              <ResultCard
                hours={hours} weeks={weeks} vph={vph} hoursLost={hoursLost}
                directCost={directCost} trueCost={trueCost} fiveYearCost={fiveYearCost}
                bucketScores={bucketScores}
              />
              <EmailCaptureCard
                first="" email=""
                hours={hours} vph={vph} trueCost={trueCost}
              />
            </div>
          </div>
        </div>
      </section>

      <Methodology />
      <FaqCalc />
      <LeakCheckCta trueCost={trueCost} />
    </>
  );
}

// -- Hero ---------------------------------------------------------------------
const Hero = () => (
  <section className="relative bg-navy text-icy overflow-hidden pt-28 pb-14" data-testid="calc-hero">
    <MotifBackground surface="hero" />
    <div className="k-container relative max-w-3xl text-center">
      <Badge tone="spectrum" className="mb-4">The Admin Tax · 60 seconds</Badge>
      <h1 className="k-h1 text-icy">How much is doing your own admin <span className="k-spectrum-text">actually costing you?</span></h1>
      <p className="k-body-l text-icy-muted mt-4 max-w-prose mx-auto">
        Most owners are stunned by this one. Slide the hours, pick your value, and we'll show you the figure that should make you angry — or at least curious enough to plug it.
      </p>
    </div>
  </section>
);

// -- Result card --------------------------------------------------------------
const ResultCard = ({ hours, weeks, vph, hoursLost, directCost, trueCost, fiveYearCost, bucketScores }) => (
  <Card className="p-6 md:p-7 bg-white overflow-hidden" accent="#EB258D" data-testid="calc-result">
    <p className="k-eyebrow text-[var(--muted)]">Your Admin Tax</p>
    <h3 className="k-h3 text-[var(--ink)] mt-1">If nothing changes…</h3>

    <div
      className="mt-5 rounded-md p-5"
      style={{ background: 'linear-gradient(135deg, rgba(235,37,141,0.10), rgba(145,86,196,0.04))' }}
    >
      <p className="k-small text-[var(--muted)]">TRUE COST (your time)</p>
      <p
        className="font-display font-bold k-spectrum-text mt-1 leading-none tabular-nums break-all"
        style={{ fontSize: 'clamp(34px, 5.6vw, 56px)', letterSpacing: '-0.02em' }}
        data-testid="calc-true-cost"
      >${trueCost.toLocaleString()}</p>
      <p className="k-small text-[var(--muted)] mt-1">per year. <span className="text-[var(--ink)] font-semibold">${fiveYearCost.toLocaleString()}</span> over 5 years.</p>
    </div>

    {/* THE MATHS — visible chain that updates live with the controls */}
    <div className="mt-5" data-testid="calc-equation">
      <p className="k-eyebrow text-[var(--muted)] mb-2">The maths</p>
      <div className="rounded-md border border-[var(--line)] bg-white p-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 font-display font-bold text-[var(--ink)] tabular-nums" style={{ fontSize: 'clamp(14px, 1.6vw, 17px)' }}>
          <EqTerm value={hours} unit="hrs/wk" leak="time" data-testid="eq-hours" />
          <span className="text-[var(--muted)] font-sans">×</span>
          <EqTerm value={weeks} unit="wks" leak="lostwork" data-testid="eq-weeks" />
          <span className="text-[var(--muted)] font-sans">×</span>
          <EqTerm value={`$${vph}`} unit="/hr" leak="money" data-testid="eq-vph" />
          <span className="text-[var(--muted)] font-sans">=</span>
          <span className="k-spectrum-text" data-testid="eq-result">${trueCost.toLocaleString()}</span>
        </div>
      </div>
    </div>

    {/* Secondary stat: direct cost @ $40/hr */}
    <div className="mt-4 rounded-md border border-[var(--line)] bg-[#F5F8FC] p-3 flex items-baseline justify-between">
      <span className="k-small text-[var(--muted)]">If you paid a bookkeeper / VA @ $40/hr</span>
      <span className="font-display font-bold text-[var(--ink)] tabular-nums">${directCost.toLocaleString()}</span>
    </div>

    <div className="mt-6 flex justify-center">
      <LeakyBucket scores={bucketScores} variant="navy-on-light" size="sm" showScore={false} />
    </div>
    <p className="k-small text-[var(--muted)] text-center mt-2">Only the Time stream is leaking in this view.</p>
  </Card>
);

const EqTerm = ({ value, unit, leak }) => (
  <span className="inline-flex items-baseline gap-1">
    <span style={{ color: leak ? `var(--leak-${leak})` : undefined }}>{value}</span>
    <span className="font-sans font-medium text-[var(--muted)] text-[13px]">{unit}</span>
  </span>
);

// -- Email capture card -------------------------------------------------------
const EmailCaptureCard = ({ hours, vph, trueCost }) => {
  const [first, setFirst] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const canSend = first.trim().length > 0 && /\S+@\S+\.\S+/.test(email) && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setBusy(true); setErr('');
    try {
      const res = await axios.post(`${API}/leads/calculator`, {
        first_name: first.trim(),
        email: email.trim(),
        admin_hours: hours,
        value_per_hour: vph,
        true_cost: trueCost,
      });
      if (res.data?.ok) {
        setDone(true);
        track('email_captured', { trueCost });
      } else {
        setErr("That didn't go through. Try again in a sec?");
      }
    } catch {
      setErr("That didn't go through. Try again in a sec?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 mt-5" data-testid="calc-email-card">
      {!done ? (
        <>
          <p className="k-eyebrow text-[var(--muted)]">Take the takeaway</p>
          <h3 className="k-h3 text-[var(--ink)] mt-1">Want this figure + the 7 admin jobs to stop doing?</h3>
          <p className="k-body text-[var(--muted)] mt-2">We'll email you the takeaway (your figure + the 7 admin jobs most owners can stop doing in a week). Plus a one-pager you can show your partner or bookkeeper.</p>
          <form onSubmit={submit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="calc-email-form">
            <Input label="First name" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="Michael" data-testid="calc-first" />
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com.au" data-testid="calc-email-input" />
            <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
              <Button type="submit" variant="primary" disabled={!canSend} data-testid="calc-email-submit">{busy ? 'Sending…' : 'Send me the takeaway'}</Button>
              <span className="k-small text-[var(--muted)]">No spam. Just useful stuff.</span>
            </div>
            {err && <p className="sm:col-span-2 k-small text-sev-high">{err}</p>}
          </form>
        </>
      ) : (
        <div className="text-center py-3" data-testid="calc-email-success">
          <div className="inline-flex h-10 w-10 rounded-pill bg-spectrum text-icy items-center justify-center"><CheckCircle2 size={20} /></div>
          <h3 className="k-h3 text-[var(--ink)] mt-3">On its way.</h3>
          <p className="k-body text-[var(--muted)] mt-1">Check your inbox in a sec.</p>
        </div>
      )}
    </Card>
  );
};

// -- Methodology --------------------------------------------------------------
const Methodology = () => (
  <section className="bg-white k-light">
    <div className="k-container k-section-tight max-w-3xl">
      <p className="k-eyebrow text-[var(--muted)] mb-3">How we got the number</p>
      <h2 className="k-h2 text-[var(--ink)]">Plain-English maths.</h2>
      <p className="k-body-l text-[var(--muted)] mt-4">
        Hours per week × working weeks per year × your value per hour. We default working weeks to 48 (a typical Aussie SMB year, allowing for holidays and slow weeks). Value per hour starts at $120 — most owners we work with land between $120 and $180.
      </p>
      <p className="k-body text-[var(--muted)] mt-3">
        The "direct cost" line above is just the same hours at the rate you'd pay a bookkeeper or VA to take them off you ($40/hr) — useful as a floor. The True Cost is the one most owners feel: <em>what those hours are worth in the work you should be doing</em>.
      </p>
      <p className="k-body text-[var(--muted)] mt-3">
        The figure is rounded to the nearest $100 — this is a thinking tool, not an audit. Your real Leak Check is the diagnostic that puts a firm dollar value on all five leaks.
      </p>
    </div>
  </section>
);

// -- FAQ ----------------------------------------------------------------------
const FAQ_ITEMS = [
  { q: 'Is this just an estimate or real?', a: "It's a rough estimate built from your numbers. The Leak Check is the real diagnostic — and it usually finds the actual figure is higher than this." },
  { q: "What if I don't know my value per hour?", a: "Pick the one that feels close. $120 is a fair default for most owner-operators in Australian SMB. The Leak Check pins this down properly." },
  { q: 'Does fixing this mean hiring a VA?', a: 'Sometimes. Usually no — most admin leaks are software + process problems, not headcount problems. Cheaper, faster, less risky.' },
  { q: 'Do I have to give you my email?', a: "No. The figure is yours either way. Email just lets us send you the takeaway + the 7 admin jobs to stop doing." },
];

const FaqCalc = () => (
  <section className="bg-[#F5F8FC] k-light">
    <div className="k-container k-section-tight max-w-[760px]">
      <p className="k-eyebrow text-[var(--muted)] mb-3">Common questions</p>
      <h2 className="k-h2 text-[var(--ink)] mb-8">About the calculator.</h2>
      <FAQ items={FAQ_ITEMS} />
    </div>
  </section>
);

// -- Closing CTA — links to Leak Check ----------------------------------------
const LeakCheckCta = ({ trueCost }) => (
  <section className="relative bg-navy text-icy overflow-hidden k-section" data-testid="calc-final-cta">
    <MotifBackground surface="honest" />
    <div className="k-container relative max-w-[60ch] mx-auto text-center">
      <div className="inline-flex h-12 w-12 rounded-md bg-spectrum text-icy items-center justify-center mb-4"><TimeLeakIcon size={22} color="#DCF0F5" /></div>
      <p className="k-eyebrow text-icy-muted mb-3">That's just one leak.</p>
      <h2 className="k-h2 text-icy">There are four more. <span className="k-spectrum-text">We find all five.</span></h2>
      <p className="k-body-l text-icy-muted mt-4">
        The Admin Tax is the easiest leak to put a number on. The Pricing Leak, the Money Leak and the Lost-Work Leak are usually bigger. The Leak Check finds all five and tells you which ones to plug first.
      </p>
      <div className="mt-7 flex justify-center">
        <Button
          as={Link}
          to="/book"
          variant="primary"
          size="lg"
          onClick={() => track('leakcheck_cta_click', { trueCost })}
          data-testid="calc-leakcheck-cta"
        >
          Book my Leak Check <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  </section>
);
